import { AdminOrganizations } from '@platform/client';
import { OrganizationApplicationStatusEnum } from '@platform/client/schemas';
import { FormsCheckBox, FormsMultilineInput, InlineFormInput } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BusinessChallengeSelectionDropdown } from '../../../components/BusinessChallengeSelectionDropdown';
import { CauseSelectionDropdown } from '../../../components/CauseSelectionDropdown';
import { CorporateClientSelectionDropdown } from '../../../components/CorporateClientSelectionDropdown';
import { Divider } from '../../../components/Divider';
import { ImpactPartnerSelectionDropdown } from '../../../components/ImpactPartnerSelectionDropdown';
import { IndustrySelectionDropdown } from '../../../components/IndustrySelectionDropdown';
import { MultipleCountrySelectionDropdown } from '../../../components/MultipleCountrySelectionDropdown';
import { OrganizationSizeSelectionDropdown } from '../../../components/OrganizationSIzeSelectionDropdown';
import { OrgTypeSelectionDropdown } from '../../../components/OrgTypeSelectionDropdown';
import { SingleCountrySelectionDropdown } from '../../../components/SingleCountrySelectionDropdown';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { AdminTableContext } from '../../AdminTable';

type EditOrganizationModalProps = {
  onClose: () => void;
  organizationId: number;
  applicationId: number;
  status: OrganizationApplicationStatusEnum;
};

type UpdateStatusFormData = {
  name?: string;
  description?: string;
  impactStory?: string;
  businessProblem?: string;
  businessSolution?: string;

  firstBusinessChallenge?: string;
  secondBusinessChallenge?: string;
  thirdBusinessChallenge?: string;

  firstBusinessChallengeDescription?: string;
  secondBusinessChallengeDescription?: string;
  thirdBusinessChallengeDescription?: string;

  website?: string;
  impactPartners?: string[];
  industries?: string[];
  countries?: string[];
  hqCountry?: string;
  causes?: string[];
  orgSize?: string;
  orgType?: string;
  registrationYear?: number;

  linkedin?: string;

  generatingRevenue?: boolean;
  corporateClient?: string;
};

export const EditOrganizationModal = (props: EditOrganizationModalProps) => {
  const { onClose, organizationId } = props;
  const { pagination } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');

  const organization = AdminOrganizations.useGetReviewData(organizationId).data;

  const { control, handleSubmit, watch, formState, register, setValue } =
    useForm<UpdateStatusFormData>({
      mode: 'onBlur',
      reValidateMode: 'onChange',
    });

  useEffect(() => {
    if (organization) {
      setValue('name', organization.name || '');
      setValue('website', organization.website || '');
      setValue('linkedin', organization.linkedin || '');
      setValue('description', organization.description || '');
      setValue('businessProblem', organization.businessProblem || '');
      setValue('businessSolution', organization.businessSolution || '');
      setValue('impactStory', organization.impactStory || '');
      setValue('firstBusinessChallenge', organization.challengeOne || '');
      setValue('secondBusinessChallenge', organization.challengeTwo || '');
      setValue('thirdBusinessChallenge', organization.challengeThree || '');
      setValue('firstBusinessChallengeDescription', organization.contextOne || '');
      setValue('secondBusinessChallengeDescription', organization.contextTwo || '');
      setValue('thirdBusinessChallengeDescription', organization.contextThree || '');
      setValue('impactPartners', organization.partners || []);
      setValue('industries', organization.industries || []);
      setValue('countries', organization.countries || []);
      setValue('causes', organization.causes || []);
      setValue('generatingRevenue', organization.generatingRevenue || false);
      setValue('hqCountry', organization.hqCountryId?.toString() || '');
      setValue('orgSize', organization.organizationSizeId?.toString() || '');
      setValue('orgType', organization.organizationTypeId?.toString() || '');
      setValue('registrationYear', organization.registrationDate || undefined);
      setValue('corporateClient', organization.corporateClient || '');
    }
  }, [organization, setValue]);

  const errors = formState.errors;

  const queryClient = useQueryClient();

  const updateMutator = AdminOrganizations.useUpdateOrganization({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminOrganizations.getGetOrganizationsQueryKey({
              page: pagination.pageIndex,
              limit: pagination.pageSize,
            }),
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [...AdminOrganizations.getGetReviewDataQueryKey(organizationId)],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<UpdateStatusFormData> = (data) => {
    updateMutator.mutate({
      data: {
        organizationId: organizationId,
        name: data.name ?? null,
        website: data.website ?? null,
        linkedin: data.linkedin ?? null,
        description: data.description ?? null,
        impactPartners: data.impactPartners ?? null,
        industries: data.industries ?? null,
        countries: data.countries ?? null,
        causes: data.causes ?? null,
        generatingRevenue: data.generatingRevenue ?? null,
        challengeOneDescription: data.firstBusinessChallengeDescription ?? null,
        challengeTwoDescription: data.secondBusinessChallengeDescription ?? null,
        challengeThreeDescription: data.thirdBusinessChallengeDescription ?? null,
        challengeOne: data.firstBusinessChallenge ?? null,
        challengeTwo: data.secondBusinessChallenge ?? null,
        challengeThree: data.thirdBusinessChallenge ?? null,
        impactStory: data.impactStory ?? null,
        corporateClient: data.corporateClient ?? null,
        organizationType: data.orgType ?? null,
        organizationSize: data.orgSize ?? null,
        registrationYear: data.registrationYear ?? null,
        businessModel: null,
        businessModelDescription: null,
        businessProblem: data.businessProblem ?? null,
        businessSolution: data.businessSolution ?? null,
        registrationCountry: data.hqCountry ?? null,
        logoUrl: null,
      },
    });
  };

  const onImpactPartnersSelectionChange = (selectedImpactPartners: string[]) => {
    setValue('impactPartners', selectedImpactPartners);
  };

  const onIndustrySelection = (selectedIndustries: string[]) => {
    setValue('industries', selectedIndustries);
  };

  const onCountrySelection = (selectedCountries: string[]) => {
    setValue('countries', selectedCountries);
  };

  const onCauseSelection = (selectedCauses: string[]) => {
    setValue('causes', selectedCauses);
  };

  return (
    <AdminFormModal
      modalStage={modalStage}
      title={`Edit ${organization?.name} (Org ID: ${organizationId})`}
      onClose={onClose}
      variant='full-screen'
      submitCTA={{ text: 'Update', callback: () => handleSubmit(onSubmit)() }}
    >
      <div className='flex flex-col gap-2 pb-8 h-[70vh]'>
        <InlineFormInput
          label='Organization name'
          placeholder="Enter organization's full name"
          register={() =>
            register('name', {
              required: 'Organization name is required',
            })
          }
          errors={errors.name}
        />
        <InlineFormInput
          label='Website'
          placeholder="Enter organization's website"
          register={() => register('website')}
          errors={errors.website}
        />
        <InlineFormInput
          label='LinkedIn URL'
          placeholder="Enter organization's LinkedIn URL"
          register={() => register('linkedin')}
          errors={errors.linkedin}
        />
        <FormsMultilineInput
          label='Description'
          control={control}
          name='description'
          formsError={formState.errors.description}
        />
        <FormsMultilineInput
          label='Business Problem'
          control={control}
          name='businessProblem'
          formsError={formState.errors.businessProblem}
        />
        <FormsMultilineInput
          label='Business Solution'
          control={control}
          name='businessSolution'
          formsError={formState.errors.businessSolution}
        />
        <FormsMultilineInput
          label='Impact Story'
          control={control}
          name='impactStory'
          formsError={formState.errors.impactStory}
        />
        <ImpactPartnerSelectionDropdown
          selectedImpactPartners={watch('impactPartners') || []}
          onImpactPartnersSelectionChange={onImpactPartnersSelectionChange}
          register={() => register('impactPartners')}
          label={'Impact Partners'}
        />
        <CorporateClientSelectionDropdown control={control} label={'Corporate Client'} />
        <div className='py-4'>
          <Divider />
        </div>
        <BusinessChallengeSelectionDropdown
          label='First Business Challenge'
          challengeNumber='first'
          control={control}
        />
        <FormsMultilineInput
          label='Challenge one context'
          control={control}
          name='firstBusinessChallengeDescription'
          formsError={formState.errors.firstBusinessChallengeDescription}
        />
        <BusinessChallengeSelectionDropdown
          label='Second Business Challenge'
          challengeNumber='second'
          control={control}
        />
        <FormsMultilineInput
          label='Challenge two context'
          control={control}
          name='secondBusinessChallengeDescription'
          formsError={formState.errors.secondBusinessChallengeDescription}
        />
        <BusinessChallengeSelectionDropdown
          label='Third Business Challenge'
          challengeNumber='third'
          control={control}
        />
        <FormsMultilineInput
          label='Challenge three context'
          control={control}
          name='thirdBusinessChallengeDescription'
          formsError={formState.errors.thirdBusinessChallengeDescription}
        />
        <Divider />
        <IndustrySelectionDropdown
          selectedIndustries={watch('industries') || []}
          onIndustriesSelectionChange={onIndustrySelection}
          label='Industries'
        />
        <MultipleCountrySelectionDropdown
          selectedCountries={watch('countries') || []}
          onCountriesSelectionChange={onCountrySelection}
          label='Countries of operations'
        />
        <CauseSelectionDropdown
          selectedCauses={watch('causes') || []}
          onCausesSelectionChange={onCauseSelection}
          label='Causes (SDGs)'
        />
        <div className='px-2'>
          <FormsCheckBox
            size='sm'
            name='generatingRevenue'
            control={control}
            description='Generating revenue'
            formsError={errors.generatingRevenue}
          />
        </div>
        <SingleCountrySelectionDropdown
          control={control}
          label='Headquarters Location'
          name='hqCountry'
        />
        <OrganizationSizeSelectionDropdown
          control={control}
          label='Organization Size'
          name='orgSize'
        />
        <OrgTypeSelectionDropdown control={control} label='Organization Type' name='orgType' />
        <InlineFormInput
          label='Year your organization was registered'
          inputType='number'
          placeholder='Enter year your organization was registered'
          register={() => register('registrationYear')}
          errors={errors.registrationYear}
        />
      </div>
    </AdminFormModal>
  );
};
